import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, variable, add, mul, pow } from './expr';

export type MonomialKey = string;

export interface Monomial {
  variables: ReadonlyMap<string, number>;
}

export class MultiPolynomial {
  readonly terms: ReadonlyMap<MonomialKey, { mono: Monomial; coeff: Fraction }>;

  private constructor(terms: Map<MonomialKey, { mono: Monomial; coeff: Fraction }>) {
    this.terms = Object.freeze(terms);
  }

  static monomialKeyFromVariables(variables: ReadonlyMap<string, number>): MonomialKey {
    if (variables.size === 0) {
      return '';
    }

    const entries = Array.from(variables.entries());
    entries.sort((a, b) => a[0].localeCompare(b[0]));

    return entries.map(([name, exp]) => `${name}:${exp}`).join(',');
  }

  static fromExpr(expr: Expr): MultiPolynomial | null {
    const result = fromExprRec(expr);
    if (result === null) {
      return null;
    }

    return new MultiPolynomial(result);
  }

  toExpr(): Expr {
    const terms: Expr[] = [];

    for (const [, { mono, coeff }] of this.terms) {
      if (coeff.num === 0) continue;

      let term: Expr;

      const varEntries = Array.from(mono.variables.entries());

      if (varEntries.length === 0) {
        term = num(coeff);
      } else {
        const varTerms: Expr[] = [];

        for (const [name, exp] of varEntries) {
          if (exp === 1) {
            varTerms.push(variable(name));
          } else {
            varTerms.push(pow(variable(name), num(exp)));
          }
        }

        const varPart = varTerms.length === 1 ? varTerms[0]! : mul(...varTerms);

        if (coeff.equals(Fraction.ONE)) {
          term = varPart;
        } else if (coeff.equals(Fraction.ONE.neg())) {
          // Represent -x as mul(-1, x)
          term = mul(num(coeff), varPart);
        } else {
          term = mul(num(coeff), varPart);
        }
      }

      terms.push(term);
    }

    if (terms.length === 0) {
      return num(Fraction.ZERO);
    }

    if (terms.length === 1) {
      return terms[0]!;
    }

    return add(...terms);
  }

  add(other: MultiPolynomial): MultiPolynomial {
    const result = new Map<MonomialKey, { mono: Monomial; coeff: Fraction }>();

    for (const [key, term] of this.terms) {
      result.set(key, { ...term });
    }

    for (const [key, otherTerm] of other.terms) {
      const existing = result.get(key);
      if (existing) {
        const newCoeff = existing.coeff.add(otherTerm.coeff);
        if (newCoeff.num !== 0) {
          result.set(key, { mono: existing.mono, coeff: newCoeff });
        } else {
          result.delete(key);
        }
      } else {
        result.set(key, { ...otherTerm });
      }
    }

    return new MultiPolynomial(result);
  }

  mul(other: MultiPolynomial): MultiPolynomial {
    const result = new Map<MonomialKey, { mono: Monomial; coeff: Fraction }>();

    for (const [, term1] of this.terms) {
      for (const [, term2] of other.terms) {
        const coeffProd = term1.coeff.mul(term2.coeff);

        if (coeffProd.num === 0) {
          continue;
        }

        const mergedVariables = new Map<string, number>();

        for (const [name, exp] of term1.mono.variables) {
          mergedVariables.set(name, exp);
        }

        for (const [name, exp] of term2.mono.variables) {
          const existing = mergedVariables.get(name) ?? 0;
          mergedVariables.set(name, existing + exp);
        }

        const resultKey = MultiPolynomial.monomialKeyFromVariables(mergedVariables);

        const existing = result.get(resultKey);
        if (existing) {
          const newCoeff = existing.coeff.add(coeffProd);
          if (newCoeff.num !== 0) {
            result.set(resultKey, { mono: { variables: mergedVariables }, coeff: newCoeff });
          } else {
            result.delete(resultKey);
          }
        } else {
          result.set(resultKey, { mono: { variables: mergedVariables }, coeff: coeffProd });
        }
      }
    }

    return new MultiPolynomial(result);
  }

  equals(other: MultiPolynomial): boolean {
    if (this.terms.size !== other.terms.size) {
      return false;
    }

    for (const [key, term] of this.terms) {
      const otherTerm = other.terms.get(key);
      if (!otherTerm) {
        return false;
      }

      if (!term.coeff.equals(otherTerm.coeff)) {
        return false;
      }

      if (term.mono.variables.size !== otherTerm.mono.variables.size) {
        return false;
      }

      for (const [name, exp] of term.mono.variables) {
        if (otherTerm.mono.variables.get(name) !== exp) {
          return false;
        }
      }
    }

    return true;
  }
}

function fromExprRec(expr: Expr): Map<string, { mono: Monomial; coeff: Fraction }> | null {
  switch (expr.kind) {
    case 'num': {
      const result = new Map<string, { mono: Monomial; coeff: Fraction }>();
      if (expr.value.num !== 0) {
        result.set('', { mono: { variables: new Map() }, coeff: expr.value });
      }
      return result;
    }

    case 'var': {
      const result = new Map<string, { mono: Monomial; coeff: Fraction }>();
      const variables = new Map<string, number>([[expr.name, 1]]);
      const key = MultiPolynomial.monomialKeyFromVariables(variables);
      result.set(key, { mono: { variables }, coeff: Fraction.ONE });
      return result;
    }

    case 'add': {
      let result: Map<string, { mono: Monomial; coeff: Fraction }> | null = null;

      for (const child of expr.children) {
        const childPoly = fromExprRec(child);
        if (childPoly === null) {
          return null;
        }

        if (result === null) {
          result = new Map(childPoly);
        } else {
          for (const [key, childTerm] of childPoly) {
            const existing = result.get(key);
            if (existing) {
              const newCoeff = existing.coeff.add(childTerm.coeff);
              if (newCoeff.num !== 0) {
                result.set(key, { mono: existing.mono, coeff: newCoeff });
              } else {
                result.delete(key);
              }
            } else {
              result.set(key, childTerm);
            }
          }
        }
      }

      return result ?? new Map();
    }

    case 'mul': {
      let result: Map<string, { mono: Monomial; coeff: Fraction }> | null = null;

      for (const child of expr.children) {
        const childPoly = fromExprRec(child);
        if (childPoly === null) {
          return null;
        }

        if (result === null) {
          result = new Map(childPoly);
        } else {
          const newResult = new Map<string, { mono: Monomial; coeff: Fraction }>();

          for (const [, term1] of result) {
            for (const [, term2] of childPoly) {
              const coeffProd = term1.coeff.mul(term2.coeff);

              if (coeffProd.num === 0) {
                continue;
              }

              const mergedVariables = new Map<string, number>();

              for (const [name, exp] of term1.mono.variables) {
                mergedVariables.set(name, exp);
              }

              for (const [name, exp] of term2.mono.variables) {
                const existing = mergedVariables.get(name) ?? 0;
                mergedVariables.set(name, existing + exp);
              }

              const resultKey = MultiPolynomial.monomialKeyFromVariables(mergedVariables);

              const existing = newResult.get(resultKey);
              if (existing) {
                const newCoeff = existing.coeff.add(coeffProd);
                if (newCoeff.num !== 0) {
                  newResult.set(resultKey, {
                    mono: { variables: mergedVariables },
                    coeff: newCoeff,
                  });
                } else {
                  newResult.delete(resultKey);
                }
              } else {
                newResult.set(resultKey, {
                  mono: { variables: mergedVariables },
                  coeff: coeffProd,
                });
              }
            }
          }

          result = newResult;
        }
      }

      return result ?? new Map([['', { mono: { variables: new Map() }, coeff: Fraction.ONE }]]);
    }

    case 'pow': {
      const basePoly = fromExprRec(expr.base);
      if (basePoly === null) {
        return null;
      }

      if (expr.exp.kind !== 'num') {
        return null;
      }

      const expFrac = expr.exp.value;
      if (!expFrac.isInteger() || expFrac.num < 0) {
        return null;
      }

      const exp = expFrac.num;

      let result = new Map<string, { mono: Monomial; coeff: Fraction }>([
        ['', { mono: { variables: new Map() }, coeff: Fraction.ONE }],
      ]);

      for (let i = 0; i < exp; i += 1) {
        const newResult = new Map<string, { mono: Monomial; coeff: Fraction }>();

        for (const [, term1] of result) {
          for (const [, term2] of basePoly) {
            const coeffProd = term1.coeff.mul(term2.coeff);

            if (coeffProd.num === 0) {
              continue;
            }

            const mergedVariables = new Map<string, number>();

            for (const [name, exp] of term1.mono.variables) {
              mergedVariables.set(name, exp);
            }

            for (const [name, exp] of term2.mono.variables) {
              const existing = mergedVariables.get(name) ?? 0;
              mergedVariables.set(name, existing + exp);
            }

            const resultKey = MultiPolynomial.monomialKeyFromVariables(mergedVariables);

            const existing = newResult.get(resultKey);
            if (existing) {
              const newCoeff = existing.coeff.add(coeffProd);
              if (newCoeff.num !== 0) {
                newResult.set(resultKey, { mono: { variables: mergedVariables }, coeff: newCoeff });
              } else {
                newResult.delete(resultKey);
              }
            } else {
              newResult.set(resultKey, { mono: { variables: mergedVariables }, coeff: coeffProd });
            }
          }
        }

        result = newResult;
      }

      return result;
    }

    case 'func':
    case 'sigma': {
      return null;
    }
  }
}
